import { Professional } from '@/containers/CustomerJourneySetup/context/types';
import { Sunrise, Sun, Sunset, Moon } from 'lucide-react';

export const generateTimeOptions = () => {
  const options: string[] = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const formattedHour = hour.toString().padStart(2, '0');
      const formattedMinute = minute.toString().padStart(2, '0');
      options.push(`${formattedHour}:${formattedMinute}`);
    }
  }
  return options;
};

export const getTimeColor = (time: string) => {
  const hour = Number.parseInt(time.split(':')[0]);
  if (hour >= 6 && hour < 12) return 'bg-blue-50 border-blue-200 text-blue-700'; // Mañana
  if (hour >= 12 && hour < 18) return 'bg-amber-50 border-amber-200 text-amber-700'; // Tarde
  if (hour >= 18 && hour < 22) return 'bg-purple-50 border-purple-200 text-purple-700'; // Tarde-noche
  return 'bg-indigo-50 border-indigo-200 text-indigo-700'; // Noche
};

export const getTimeIcon = (time: string) => {
  const hour = Number.parseInt(time.split(':')[0]);
  if (hour >= 6 && hour < 12) return <Sunrise className="h-3 w-3" />;
  if (hour >= 12 && hour < 18) return <Sun className="h-3 w-3" />;
  if (hour >= 18 && hour < 22) return <Sunset className="h-3 w-3" />;
  return <Moon className="h-3 w-3" />;
};

export const getInitials = (name: string) => {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);
};

export const getAvatarColor = (id: string) => {
  const colors = [
    'bg-blue',
    'bg-green-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-yellow-500',
    'bg-red-500',
    'bg-indigo-500'
  ];

  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash);
  }

  return colors[Math.abs(hash) % colors.length];
};

export const timeToMinutes = (time: string) => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

export const hasTimeOverlap = (day: string, slots: { start: string; end: string }[]) => {
  for (let i = 0; i < slots.length; i++) {
    for (let j = i + 1; j < slots.length; j++) {
      const a = slots[i];
      const b = slots[j];

      const aStart = timeToMinutes(a.start);
      const aEnd = timeToMinutes(a.end);
      const bStart = timeToMinutes(b.start);
      const bEnd = timeToMinutes(b.end);

      if ((aStart <= bStart && bStart < aEnd) || (bStart <= aStart && aStart < bEnd)) {
        return true;
      }
    }
  }
  return false;
};

// Get detailed conflict information for better error messages
export const getConflictDetails = (slots: { start: string; end: string }[]) => {
  const conflicts: Array<{
    slot1: { start: string; end: string };
    slot2: { start: string; end: string };
    overlapStart: string;
    overlapEnd: string;
  }> = [];

  for (let i = 0; i < slots.length; i++) {
    for (let j = i + 1; j < slots.length; j++) {
      const a = slots[i];
      const b = slots[j];

      const aStart = timeToMinutes(a.start);
      const aEnd = timeToMinutes(a.end);
      const bStart = timeToMinutes(b.start);
      const bEnd = timeToMinutes(b.end);

      if ((aStart <= bStart && bStart < aEnd) || (bStart <= aStart && aStart < bEnd)) {
        const overlapStart = Math.max(aStart, bStart);
        const overlapEnd = Math.min(aEnd, bEnd);

        conflicts.push({
          slot1: a,
          slot2: b,
          overlapStart: `${Math.floor(overlapStart / 60)
            .toString()
            .padStart(2, '0')}:${(overlapStart % 60).toString().padStart(2, '0')}`,
          overlapEnd: `${Math.floor(overlapEnd / 60)
            .toString()
            .padStart(2, '0')}:${(overlapEnd % 60).toString().padStart(2, '0')}`
        });
      }
    }
  }
  return conflicts;
};

// Filter end time options based on start time
export const getFilteredEndTimeOptions = (startTime: string, allTimeOptions: string[]) => {
  if (!startTime) return allTimeOptions;

  const startMinutes = timeToMinutes(startTime);
  return allTimeOptions.filter((time) => timeToMinutes(time) > startMinutes);
};

// Get the minimum start time for a new slot based on existing slots
export const getMinimumStartTime = (existingSlots: { start: string; end: string }[]) => {
  if (existingSlots.length === 0) return null;

  // Sort slots by start time
  const sortedSlots = [...existingSlots].sort((a, b) => timeToMinutes(a.start) - timeToMinutes(b.start));

  // Get the latest end time
  const latestEndTime = sortedSlots.reduce((latest, slot) => {
    const currentEndMinutes = timeToMinutes(slot.end);
    const latestMinutes = timeToMinutes(latest);
    return currentEndMinutes > latestMinutes ? slot.end : latest;
  }, '00:00');

  return latestEndTime;
};

// Filter start time options based on existing slots to prevent overlaps
export const getFilteredStartTimeOptions = (
  allTimeOptions: string[],
  existingSlots: { start: string; end: string }[],
  currentSlotIndex?: number
) => {
  if (existingSlots.length === 0) return allTimeOptions;

  // Get slots excluding the current one being edited
  const otherSlots =
    currentSlotIndex !== undefined
      ? existingSlots.filter((_, index) => index !== currentSlotIndex)
      : existingSlots;

  // If we're adding a new slot (not editing), filter from the end of the last slot
  if (currentSlotIndex === undefined) {
    const minimumStartTime = getMinimumStartTime(otherSlots);
    if (minimumStartTime) {
      const minimumStartMinutes = timeToMinutes(minimumStartTime);
      return allTimeOptions.filter((time) => timeToMinutes(time) >= minimumStartMinutes);
    }
  }

  return allTimeOptions.filter((time) => {
    const timeMinutes = timeToMinutes(time);

    // Check if this time would conflict with any existing slot
    return !otherSlots.some((slot) => {
      const slotStart = timeToMinutes(slot.start);
      const slotEnd = timeToMinutes(slot.end);
      return timeMinutes >= slotStart && timeMinutes < slotEnd;
    });
  });
};

export const hasProfessionalSchedule = (professional: Professional) => {
  return professional.schedule && Object.values(professional.schedule).some((slots) => slots.length > 0);
};

// Contar el número total de slots de horario para un profesional
export const countTotalTimeSlots = (professional: Professional) => {
  return Object.values(professional.schedule).reduce((total, slots) => total + slots.length, 0);
};
